import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Navigation as NavIcon, Plus, Trash2, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { AdminSection } from "@/components/admin/AdminSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  listNavItems,
  saveNavItem,
  deleteNavItem,
  reorderNavItems,
} from "@/lib/admin/cms.functions";

export const Route = createFileRoute("/_admin/admin/cms/navigation")({
  component: NavigationPage,
});

type NavRow = {
  id: string;
  menu: string;
  label: string;
  href: string;
  is_external: boolean;
  is_visible: boolean;
  display_order: number;
};

type Draft = {
  id?: string;
  menu: "header" | "footer" | "mobile";
  label: string;
  href: string;
  is_external: boolean;
  is_visible: boolean;
  display_order: number;
};

const MENUS: { key: Draft["menu"]; label: string; hint: string }[] = [
  { key: "header", label: "Header menu", hint: "Main navigation shown on desktop." },
  { key: "mobile", label: "Mobile menu", hint: "Items shown in the mobile drawer." },
  { key: "footer", label: "Footer menu", hint: "Secondary links in the footer bar." },
];

function NavigationPage() {
  const list = useServerFn(listNavItems);
  const save = useServerFn(saveNavItem);
  const remove = useServerFn(deleteNavItem);
  const reorder = useServerFn(reorderNavItems);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["cms-admin", "nav"],
    queryFn: () => list(),
  });
  const rows = useMemo(() => (data ?? []) as unknown as NavRow[], [data]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "nav"] });
    void qc.invalidateQueries({ queryKey: ["cms", "navigation"] });
  };

  const saveMutation = useMutation({
    mutationFn: (d: Draft) => save({ data: d }),
    onSuccess: () => {
      toast.success("Menu item saved");
      setDraft(null);
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Menu item removed");
      refresh();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the menu item."),
  });

  const moveMutation = useMutation({
    mutationFn: (items: { id: string; display_order: number }[]) => reorder({ data: { items } }),
    onSuccess: refresh,
  });

  const move = (menu: string, id: string, dir: -1 | 1) => {
    const group = rows.filter((r) => r.menu === menu).sort((a, b) => a.display_order - b.display_order);
    const idx = group.findIndex((r) => r.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= group.length) return;
    const next = [...group];
    const a = next[idx]!;
    const b = next[target]!;
    next[idx] = b;
    next[target] = a;
    moveMutation.mutate(next.map((r, i) => ({ id: r.id, display_order: i })));
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Navigation"
        description="Control the links shown in the header, mobile drawer and footer bar."
        actions={<ImportButtons moduleId="navigation" />}
      />
      {isLoading ? <p className="text-sm text-muted-foreground">Loading menu…</p> : null}

      {MENUS.map((menu, i) => {
        const items = rows
          .filter((r) => r.menu === menu.key)
          .sort((a, b) => a.display_order - b.display_order);
        return (
          <AdminSection
            key={menu.key}
            number={i + 1}
            icon={NavIcon}
            title={menu.label}
            description={menu.hint}
            customised={items.length > 0}
            actions={
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() =>
                  setDraft({
                    menu: menu.key,
                    label: "",
                    href: "/",
                    is_external: false,
                    is_visible: true,
                    display_order: items.length,
                  })
                }
              >
                <Plus className="h-3.5 w-3.5" aria-hidden /> Add
              </Button>
            }
          >
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items yet.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {items.map((item, idx) => (
                  <li key={item.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <span className="min-w-40 flex-1 text-sm font-medium text-foreground">
                      {item.label}
                    </span>
                    <span className="min-w-40 flex-1 truncate text-xs text-muted-foreground">
                      {item.href}
                    </span>
                    {!item.is_visible ? (
                      <span className="rounded border px-1.5 text-[10px] uppercase text-muted-foreground">
                        Hidden
                      </span>
                    ) : null}
                    {item.is_external ? (
                      <span className="rounded border px-1.5 text-[10px] uppercase text-muted-foreground">
                        New tab
                      </span>
                    ) : null}
                    <div className="ml-auto flex items-center gap-1">
                      <Button size="icon" variant="ghost" disabled={idx === 0} onClick={() => move(menu.key, item.id, -1)}>
                        <ArrowUp className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button size="icon" variant="ghost" disabled={idx === items.length - 1} onClick={() => move(menu.key, item.id, 1)}>
                        <ArrowDown className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setDraft({
                            id: item.id,
                            menu: menu.key,
                            label: item.label,
                            href: item.href,
                            is_external: item.is_external,
                            is_visible: item.is_visible,
                            display_order: item.display_order,
                          })
                        }
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this menu item?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This removes "{item.label}" permanently. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMutation.mutate(item.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </AdminSection>
        );
      })}

      <Dialog open={draft !== null} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit menu item" : "Add menu item"}</DialogTitle>
          </DialogHeader>
          {draft ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="nav-label" className="text-xs">Label</Label>
                <Input id="nav-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nav-href" className="text-xs">Link</Label>
                <Input id="nav-href" value={draft.href} onChange={(e) => setDraft({ ...draft, href: e.target.value })} placeholder="/meal-plans" />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Open in new tab</span>
                <Switch checked={draft.is_external} onCheckedChange={(v) => setDraft({ ...draft, is_external: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Visible on site</span>
                <Switch checked={draft.is_visible} onCheckedChange={(v) => setDraft({ ...draft, is_visible: v })} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button
              disabled={!draft?.label.trim() || !draft?.href.trim() || saveMutation.isPending}
              onClick={() => draft && saveMutation.mutate(draft)}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
